import { formatCNPJ } from '@brazilian-utils/brazilian-utils';
import { Box, Chip, Typography } from '@mui/material';
import moment from 'moment';
import React, { RefObject, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrayField,
  AutocompleteArrayInput,
  BooleanField,
  Button,
  Datagrid,
  DateField,
  EditButton,
  EmailField,
  Filter,
  FunctionField,
  Labeled,
  List,
  ListBase,
  ListContextProvider,
  ListToolbar,
  NumberField,
  Pagination,
  RaRecord,
  RecordContextProvider,
  ReferenceArrayField,
  ReferenceArrayInput,
  ReferenceField,
  ReferenceManyField,
  SelectArrayInput,
  SelectField,
  SelectInput,
  Show,
  SimpleShowLayout,
  Tab,
  TabbedShowLayout,
  TextField,
  TopToolbar,
  useDataProvider,
  useGetList,
  useList,
  useListContext,
  useNotify,
  usePermissions,
  useRecordContext,
  useRefresh,
  useTranslate,
  useUpdate
} from "react-admin";
import { AddCoupon, BalanceButton, CheckChainUsesModule, CopyToChipboardField, CustomerUnifiedSendButton, CustomExportButton, CustomizableDatagrid, DeviceDisableButton, FillinsDatagrids, LimitField, ModuleKey, Permission, PrintButton, RedeemCouponAdminButton, RemoveCouponButton, RemovePointsButton, ReportList, ReportPrint, StarRatingField } from '../../components';
import { BetweenDatesInput } from '../../components/BetweenDatesInput';
import { CPFField, PartiallyHiddenCpfField, PhoneField } from "../../components/Fields";
import ListPagination from '../../components/ListPagination';
import RefundButton from '../../components/RefundButton';
import { PERMISSIONS } from '../../constants';
import { getEmployeeType, getPlaceId, getUserId, isFreeCashbackCoupon } from '../../lib';
import { DeviceStateType, EmployeeType, FillinOrigin, TransactionType } from '../../models';
import { filterToQuery, formatAttendantLabel, formatPoints } from '../../utils';
import { DrawingDatagrid } from '../drawings/DrawingList';
import { PotencialFraudDataGrid } from '../potencialFraud/PotencialFraudList';
import { RatingDatagrid } from '../ratings/ratingList';
import { ClientLogsTab } from './ClientLogsTab';
import CustomerAchievementsList from './CustomerAchievementsList';
import { __t } from '../../i18n/t';

type DataType = Array<{ [key: string]: any }>;
type RelationType = { field: string, resource: string };

const listMarginSx = {
  marginTop: '18px'
};

const filterTablePrintSx = {
  display: 'none',
  '@media print': {
    display: 'block'
  },
  marginBottom: '10px',
};

const titleCardSx = {
  textAlign: 'center',
  fontWeight: 'bold',
};

const ClientShowActions = (props) => (
  <TopToolbar>
    <Permission permission={PERMISSIONS.UPDATE_CUSTOMER}>
      <EditButton basePath={props.basePath} record={props.data} />
    </Permission>
  </TopToolbar>
);

// const TransactionRedemptionsList = (props) => {
//   console.log('props: ', props)
//   switch (props.record.type) {
//     case TransactionType.redemption:
//       return (
//         <List>
//         <ReferenceManyField source="coupons" target="coupons" reference="/coupons">
//             <Datagrid rowClick="show">
//                 <TextField source="acronym" label="Sigla" />
//                 <TextField source="name" label="Nome" />
//             </Datagrid>
//         </ReferenceManyField>
//     </SimpleShowLayout>
// </Show>
//       );
//     default:
//       return (
//         <ReferenceManyField
//           reference="transaction-redemptions"
//           target="transactionId"
//           addLabel={false}
//           sort={{ field: "createdAt", order: "DESC" }}
//           pagination={<Pagination />}
//         >
//           <Datagrid expand={<TransactionRedemptionsList />}>
//             <NumberField source="value" label="Valor" />
//             <FunctionField render={record => translateTransactionType(record.type)} label='Tipo' />
//             <NumberField source="balance" label="Saldo Parcial" />
//             <DateField source="createdAt" label="Data" showTime />
//           </Datagrid>
//         </ReferenceManyField>
//       )
//   }
// };

const RefundButtonField = (props) => {
  const coupon = useRecordContext();
  if (!coupon) { return <></>; }
  return (
    coupon ?
      coupon.redeemed ? <FunctionField render={record => "Cupom Utilizado"} />
        : coupon.refunded ? <FunctionField render={record => "Cupom estornado"} />
          :
          <Permission permission={PERMISSIONS.REFUND_COUPON}>
            <RefundButton record={coupon} customerId={props.customerId} />
          </Permission>
      : null
  );
};

const CheckExistenceOfData = ({ children }) => {
  const { data } = useListContext();
  if (!data || data.length === 0) {
    return <></>;
  }

  return (React.cloneElement(children));
};

const FillinList = ({ fillinId }) => {
  const dataProvider = useDataProvider();
  const [fillin, setFillin] = useState<any[]>([]);

  useEffect(() => {
    dataProvider.getList('fillins', {
      filter: { id: fillinId, skipJoins: true },
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'id', order: 'ASC' },
    }).then(({ data }) => {
      setFillin(data[0]?.data || []);
    });
  }, [fillinId]);

  if (!fillin.length) return null;

  return (
    <>
      <Typography style={{ marginTop: 20 }} variant="body1" component="h4" align="center">Abastecimento</Typography>
      <Datagrid
        data={fillin}
        bulkActionButtons={false}
        empty={<Typography style={{ marginTop: 20 }} variant="body2" component="h4" align="center">Não há abastecimento</Typography>}
      >
        <ReferenceField
          label="Posto"
          emptyText="Posto não identificado"
          source="placeId"
          reference="places"
          link={false}
        >
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField
          label="Frentista"
          emptyText="Não identificado"
          source="attendantId"
          reference="attendants"
          link={false}
        >
          <TextField source="name" />
        </ReferenceField>
        <NumberField
          source="value"
          label="Valor"
          textAlign="center"
          emptyText="R$ 0,000"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          }}
        />
        <NumberField
          source="amount"
          label="Litros"
          textAlign="center"
          emptyText="0"
          options={{
            maximumFractionDigits: 2
          }}
        />
        <NumberField
          source="hoseNumber"
          label="Bico"
          textAlign="center"
          emptyText="Não informado"
        />
        <NumberField
          source="encerrante"
          label="Encerrante"
          textAlign="center"
          emptyText="Não informado"
          options={{
            maximumFractionDigits: 2
          }}
        />
        <NumberField
          source="pricePerLiter"
          label="Preço com desconto"
          textAlign="center"
          emptyText="R$ 0,000"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 3,
            maximumFractionDigits: 3
          }}
        />
        <NumberField
          source="oldPricePerLiter"
          label="Preço normal"
          textAlign="center"
          emptyText="R$ 0,000"
          options={{
            style: 'currency',
            currency: 'BRL',
            minimumFractionDigits: 3,
            maximumFractionDigits: 3
          }}
        />
        <FunctionField label="Desconto" textAlign="center" render={record => record && `${(record.oldPricePerLiter && record.pricePerLiter ? (record.oldPricePerLiter - record.pricePerLiter) * (record.amount || 0) : 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}`} />
        <ReferenceField source="discountId" reference="discounts" label="Código do desconto" textAlign="center" emptyText="--">
          <TextField source="identify" />
        </ReferenceField>
        <ReferenceField source="scoringId" reference="scorings" label="Código da pontuação" textAlign="center" emptyText="--" link="show">
          <TextField source="identify" />
        </ReferenceField>
        <ReferenceField label="Combustível" emptyText="Não identificado" source="fuelId" reference="fuels" link={false}>
          <TextField source="name" />
        </ReferenceField>
      </Datagrid>
    </>
  );
};

const TransactionPanel = React.memo(({ customerId }: any) => {
  const record = useRecordContext();
  if (!record) { return null; }

  const SalePanel = (props) => {
    const saleRecord = useRecordContext();
    const salePartnerProducts = useMemo(
      () => (Array.isArray(saleRecord?.salePartnerProducts) ? saleRecord.salePartnerProducts : []).map((item, index) => ({
        id: item?.id ?? `${saleRecord?.id ?? 'sale'}-${index}`,
        ...item,
      })),
      [saleRecord]
    );

    const salePartnerProductsListContext = useList({
      data: salePartnerProducts,
      isLoading: false,
      isFetching: false,
      isPending: false,
      perPage: salePartnerProducts.length || 1000,
      resource: 'sale-partner-products',
      selectedIds: [],
    });

    return (
      <ListContextProvider value={salePartnerProductsListContext}>
        <Datagrid bulkActionButtons={false} resource="sale-partner-products">
          <FunctionField label="Produto" render={record => record && (record.fuel ? record.fuel.name : record.saleProduct ? record.saleProduct.name : 'Não identificado')} textAlign="center" />
          <NumberField label="Volume" source="amount" textAlign="center" />
          <NumberField label="Pontos" source="points" textAlign="center" />
        </Datagrid>
      </ListContextProvider>
    );
  };

  const ProductField = (props) => {
    const record = useRecordContext();
    if (!record) { return null; }

    if (record.title) {
      return (
        <TextField
          source="title"
          sortable={false}
        />
      )
    }

    return (
      isFreeCashbackCoupon(record) ?
        <FunctionField render={() => 'Cashback'} />
        :
        <ReferenceField reference="chains/chainId/products" source="productId" link={false} textAlign="center">
          <TextField source="name" />
        </ReferenceField>
    );
  };

  return (
    <div style={{ paddingBottom: 20 }}>
      {(record.fillin_id && record.type !== TransactionType.marketing) && <FillinList fillinId={record.fillin_id} />}

      {(record.type === TransactionType.adition || record.type === TransactionType.manualAddition) &&
        <SimpleShowLayout>
          <Box display="flex" style={{ gap: 32 }}>
            <Labeled>
              <ReferenceField label="Funcionário" source="employee_id" reference="employees" emptyText="-">
                <TextField source="name" />
              </ReferenceField>
            </Labeled>
            <Labeled>
              <TextField source="coupon_invoice" label="Número do cupom" textAlign="center" emptyText="--" sortable={false} />
            </Labeled>
            <Labeled>
              <TextField source="coupon_serial_number" label="Série do cupom" textAlign="center" emptyText="--" sortable={false} />
            </Labeled>
          </Box>
        </SimpleShowLayout>
      }

      {(record.type === TransactionType.sale || record.type === TransactionType.refuelNSale || (record.type === TransactionType.refuel && !record.fillin_id)) &&
        < Permission permission={PERMISSIONS.LIST_SALES}>
          <ReferenceManyField reference="sales" target="transactionId" emptyText="Nenhum item foi encontrado">
            <CheckExistenceOfData>
              <>
                <Typography style={{ marginTop: 30 }} variant="body1" component="h4" align="center">Vendas</Typography>
                <Datagrid expand={<SalePanel />} bulkActionButtons={false}>
                  <NumberField source="netValue" label="Valor" textAlign="center" emptyText="R$ 0,00" options={{ style: 'currency', currency: 'BRL' }} />
                  <NumberField source="totalValue" label="Total" textAlign="center" emptyText="R$ 0,00" options={{ style: 'currency', currency: 'BRL' }} />
                  <FunctionField label="Status" render={record => record?.status === 'active' ? 'ATIVA' : record?.status === 'canceled' ? 'CANCELADO' : ''} textAlign="center" />
                </Datagrid>
              </>
            </CheckExistenceOfData>
          </ReferenceManyField>
        </Permission>
      }

      <ReferenceManyField label="Coupons" reference="coupons" target="transaction_id" sort={{ field: 'createdAt', order: 'DESC' }}>
        <CheckExistenceOfData>
          <>
            <Typography style={{ marginTop: 30 }} variant="body1" component="h4" align="center">Cupons</Typography>
            <Datagrid bulkActionButtons={false}>
              <TextField source="barcode" />
              <ProductField label="Produto" />
              <NumberField source="value" label="Valor" textAlign="center" />
              <DateField source="refundedAt" label="Data do Reembolso" showTime textAlign="center" locales="pt-BR" />
              <RefundButtonField customerId={customerId} />
              <ReferenceField
                source="redemptionEmployeeId"
                reference="employees"
                label="Funcionário"
                textAlign="center"
                link={false}
                sortable={false}
              >
                <TextField source="name" />
              </ReferenceField>
            </Datagrid>
          </>
        </CheckExistenceOfData>
      </ReferenceManyField>
      {
        record.rule_id &&
        <Labeled label="Motivo da Recompensa" sx={{ marginTop: 4 }} >
          <ReferenceField source="rule_id" reference="chains/chainId/rules" emptyText="Não identificado">
            <TextField source="label" />
          </ReferenceField>
        </Labeled>
      }
      {
        (record.type === TransactionType.marketing && record.fillin_id) &&
        <Permission permission={PERMISSIONS.LIST_RATINGS}>
          <>
            <Typography style={{ marginTop: 10 }} variant="body1" component="h4" align="center">Avaliação</Typography>
            <ReferenceManyField source="fillin_id" reference="ratings" target="fillinId" label="Avaliação">
              <RatingDatagrid showCustomerColumn={false} />
            </ReferenceManyField>
          </>
        </Permission>
      }
    </div >
  );
});

const StatementDatagrid = ({ customerId, chainId }) => (
  <CustomizableDatagrid
    resource={`chains/${chainId}/customers/transactions`}
    bulkActionButtons={false}
    expand={<TransactionPanel customerId={customerId} />}
    defaultColumns={['created_at', 'value', 'type', 'partial_balance', 'fillin_fuel_id', 'place_id', 'company_id', 'attendant_id', 'expires_at']}
    isRowExpandable={record => record && [TransactionType.adition, TransactionType.manualAddition, TransactionType.refuel, TransactionType.refuelNSale, TransactionType.manualRefuel, TransactionType.marketing, TransactionType.redemption, TransactionType.sale,
    TransactionType.firstRefuel, TransactionType.firstMonth, TransactionType.firstWeek, TransactionType.birthdayDay, TransactionType.birthdayMonth, TransactionType.completeRegistration,].includes(record.type)}
  >
    <DateField source="created_at" label="Data" sortable={false} showTime locales="pt-BR" />
    <NumberField source="value" label="Pontos" sortable={false} textAlign="center" />
    <FunctionField source="type" render={record => TransactionType.transalateType(record ? record.type : null)} label="Tipo" textAlign="center" sortable={false} />
    <FunctionField
      source="partial_balance"
      label="Saldo Parcial"
      textAlign="center"
      sortable={false}
      render={record => {
        const value = Number(record?.partial_balance) || 0;
        const rounded = Math.round(value * 100) / 100;
        const formatted = Object.is(rounded, -0) ? 0 : rounded;
        return formatted.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
      }}
    />
    <ReferenceField label="Produto" reference="fuels" source="fillin_fuel_id" link={false} emptyText="-">
      <TextField source="name" />
    </ReferenceField>
    <ReferenceField label="Local" source="place_id" reference="places" link={false} sortable={false} emptyText="-">
      <TextField source="name" />
    </ReferenceField>
    <ReferenceField label="Empresa" source="company_id" reference="companies" link={false} sortable={false} emptyText="-">
      <FunctionField render={record => formatAttendantLabel(record)} />
    </ReferenceField>
    <ReferenceField
      label="Frentista"
      source="attendant_id"
      reference="attendants"
      textAlign="center"
      emptyText="-"
      link={false}
      sortable={false}
    >
      <FunctionField render={record => formatAttendantLabel(record)} />
    </ReferenceField>
    <DateField source="expires_at" label="Expira em" sortable={false} showTime locales="pt-BR" />
    <TextField source="description" label="Descrição" emptyText="-" />
    {
      CheckChainUsesModule({
        module: ModuleKey.LIMITS, children: (
          <LimitField source="limit_id" label="Limite" />
        )
      }) || <></>
    }
    <FunctionField source="system_origin" render={record => FillinOrigin.translateOrigin(record ? record.system_origin : null)} label="Origem" textAlign="center" sortable={false} />
    <RemovePointsButton />
  </CustomizableDatagrid>
);

const StatementsActions: React.FC<{ reportRef?: RefObject<HTMLDivElement> | null }> = ({ reportRef }) => {
  const dataProvider = useDataProvider();
  const preExporter = async (
    statements: DataType,
    fetchRelatedRecords: (relations: Array<RelationType>) => Promise<(RelationType & { results: DataType; })[]>
  ) => {
    const relations = [
      { field: 'fillin_fuel_id', resource: 'fuels' },
      { field: 'place_id', resource: 'places' },
      { field: 'company_id', resource: 'companies' },
      { field: 'employee_id', resource: 'employees' },
      { field: 'fillin_attendant_id', resource: 'attendants' },
      { field: 'limit_id', resource: 'point-rule' },
    ];
    const IDS_MAX_LENGTH = 240;
    const filterTransactionsIds = statements.filter((statement) => statement.type !== TransactionType.discount && statement.type !== TransactionType.canceled && statement.type !== TransactionType.refund && statement.type !== TransactionType.expiration).map(({ id }) => id);
    const folds = Math.ceil(filterTransactionsIds.length / IDS_MAX_LENGTH);

    const promises: RaRecord[] = [];
    const salesData: RaRecord[] = [];
    const couponsData: RaRecord[] = [];

    for (let i = 0; i < folds; i++) {
      const start = i * IDS_MAX_LENGTH; //   0, 240, 0, ...
      const end = (i + 1) * IDS_MAX_LENGTH - 1; // 239, 478, ...

      promises.push(
        //@ts-ignore
        dataProvider.getList('sales', { filter: { transactionIds: filterTransactionsIds.slice(start, end) } })
          .then(({ data }) => salesData.push(...data))
      );
      promises.push(
        //@ts-ignore
        dataProvider.getList('coupons', { filter: { transactionIds: filterTransactionsIds.slice(start, end) } })
          .then(({ data }) => couponsData.push(...data))

      );
    }
    await Promise.all(promises);
    const fetchData = await fetchRelatedRecords(relations);

    const getData = (resource: string) => {
      return fetchData.find(({ resource: res }) => res === resource)?.results || [];
    };

    const employeeIds = couponsData.filter((c) => c.redemptionEmployeeId).map((c) => c.redemptionEmployeeId);

    const { data: redemptionEmployee } = await dataProvider.getMany('employees', { ids: employeeIds });

    statements = statements.reduce((acc: Array<{}>, { ...statement }) => {
      const sale = salesData.find((sale) => sale.transactionId === statement.id);
      if (sale?.salePartnerProducts) {
        return [...acc, ...sale.salePartnerProducts.map((s) => {
          const prefixSale = Object.keys((s)).reduce((acc, key) => {
            acc[`sale_${key}`] = s[key];
            return acc;
          }, {});
          prefixSale['sale_totalValue'] = sale?.totalValue;
          prefixSale['sale_netValue'] = sale?.netValue;
          prefixSale['sale_status'] = sale?.status;
          return { ...statement, ...prefixSale };
        })];
      }

      const coupons = couponsData.filter((c) => c.transactionId === statement.id);

      if (coupons.length) {
        return [...acc, ...coupons.map((c) => {
          const prefixCoupon = Object.keys((c)).reduce((acc, key) => {
            acc[`coupon_${key}`] = c[key];
            return acc;
          }, {});
          return { ...statement, ...prefixCoupon };
        })];
      }

      return [...acc, { ...statement }];
    }, []);

    return statements.map((statement) => {
      const { place_id, company_id, fillin_fuel_id, fillin_attendant_id, employee_id, limit_id, id } = statement;
      const fuel = getData('fuels').find(({ id }) => id === fillin_fuel_id);
      const place = getData('places').find(({ id }) => id === place_id);
      const company = getData('companies').find(({ id }) => id === company_id);
      const attendant = getData('attendants').find(({ id }) => id === fillin_attendant_id);
      const employee = getData('employees').find(({ id }) => id === employee_id);
      const couponEmployee = redemptionEmployee.find((e) => e.id === statement.coupon_redemptionEmployeeId);
      const pointRule = getData('point-rule').find(({ id }) => id === limit_id);

      return {
        id,
        data: statement.created_at,
        pontos: statement.value,
        tipo: TransactionType.transalateType(statement.type),
        saldo_parcial: statement.partial_balance,
        produto: fuel ? fuel.name : '-',
        local: place ? place.name : '-',
        empresa: company ? company.name || formatCNPJ(company.cnpj) : '-',
        expira_em: statement.expires_at,
        origem: FillinOrigin.translateOrigin(statement.origin),
        limite: pointRule ? pointRule.identify : '-',
        descricao: statement.description,
        abastecimento_frentista: attendant ? attendant.name : '-',
        abastecimento_valor: statement.fillin_value ? statement.fillin_value : '-',
        abastecimento_litros: statement.fillin_amount ? statement.fillin_amount : '-',
        abastecimento_bico: statement.fillin_hose_number ? statement.fillin_hose_number : '-',
        abastecimento_encerrante: statement.fillin_encerrante ? statement.fillin_encerrante : '-',
        abastecimento_preco_com_desconto: statement.fillin_price_per_liter ? statement.fillin_price_per_liter : '-',
        abastecimento_preco_normal: statement.fillin_old_price_per_liter ? statement.fillin_old_price_per_liter : '-',
        abastecimento_desconto: statement.fillin_discount ? statement.fillin_discount : '-',
        abastecimento_combustivel: fuel ? fuel.name : '-',
        funcionario: employee ? employee.name : '-',
        venda_valor: statement.sale_netValue ? statement.sale_netValue : '-',
        venda_total: statement.sale_totalValue ? statement.sale_totalValue : '-',
        venda_status: statement.sale_status ? statement.sale_status === 'active' ? 'ATIVA' : statement.sale_status === 'canceled' ? 'CANCELADO' : '' : '-',
        venda_volume: statement.sale_amount ? statement.sale_amount : '-',
        venda_pontos: statement.sale_points ? statement.sale_points : '-',
        venda_produto: statement.sale_fuel ? statement.sale_fuel.name : statement.sale_saleProduct ? statement.sale_saleProduct.name : '-',
        cupom_barcode: statement.coupon_barcode ? statement.coupon_barcode : '-',
        // Produto em dinheiro também tem coupon_isCashback = true, por isso o produto vem primeiro.
        cupom_produto: statement.coupon_product ? statement.coupon_product.name : statement.coupon_isCashback ? 'cashback' : '-',
        cupom_valor: statement.coupon_value ? statement.coupon_value : '-',
        cupom_data_do_reembolso: statement.coupon_refundedAt ? statement.coupon_refundedAt : '-',
        cupom_funcionario: couponEmployee ? couponEmployee.name : '-',
        cupom_numero_do_cupom: statement.type === TransactionType.adition ? statement.coupon_invoice || '-' : '-',
        cupom_serie_do_cupom: statement.type === TransactionType.adition ? statement.coupon_serial_number || '-' : '-',
      };
    });

  };

  return (
    <TopToolbar>
      <CustomExportButton fileName='Extrato' preExport={preExporter} isReport={false} />
      <PrintButton printRef={reportRef} />
    </TopToolbar>
  );
};

const ListStatements = () => {
  const record = useRecordContext();
  if (!record) { return null; }

  const CustomersFilters = () => {
    return (
      <Filter>
        <BetweenDatesInput alwaysOn />
        <SelectInput
          label="Tipo"
          source="type"
          choices={TransactionType.transactionTypeChoices}
          alwaysOn
        />
      </Filter>
    )
  }

  const FilterTablePrint = () => {
    const { filterValues } = useListContext();
    const record = useRecordContext();

    return (
      <Datagrid data={[{ ...filterValues, name: record.name, cpf: record.cpf, generatedReportAt: moment().toISOString() }]} total={1} bulkActionButtons={false}>
        <TextField source="name" textAlign="center" label="Nome" sortable={false} />
        {/* @ts-ignore */}
        <CPFField source="cpf" textAlign="center" label="CPF" sortable={false} />
        <DateField source="from" textAlign="center" label="De" sortable={false} locales="pt-BR" />
        <DateField source="to" textAlign="center" label="Até" sortable={false} locales="pt-BR" />
        <SelectField label="Tipo" source="type" choices={TransactionType.transactionTypeChoices} emptyText="--" textAlign="center" />
        <DateField source="generatedReportAt" textAlign="center" label="Gerado às" showTime sortable={false} locales="pt-BR" />
      </Datagrid>
    );
  }

  return (
    //@ts-ignore
    <ReportList
      title=' '
      resource={`chains/${record.chainId}/customers/transactions`}
      sort={{ field: "created_at", order: "DESC" }}
      filters={<CustomersFilters />}
      filterDefaultValues={{
        from: moment().startOf("day").subtract(1, "month").toISOString(),
        to: moment().endOf("day").toISOString(),
      }}
      filter={{ customerId: record.id }}
      exporter={false}
      disableSyncWithLocation
      actions={<StatementsActions />}
      titleOnPrint="Extrato"
      filterTablePrint={<FilterTablePrint />}
      landscape
    >
      <StatementDatagrid customerId={record.id} chainId={record.chainId} />
    </ReportList>
  );
}

const FilterTablePrint = ({ customerName }) => {
  const { filterValues } = useListContext();

  return (
    <Datagrid bulkActionButtons={false} data={[{ ...filterValues, customerName }]} total={1}>
      <DateField source="from" textAlign="center" label="De" sortable={false} locales="pt-BR" />
      <DateField source="to" textAlign="center" label="Até" sortable={false} locales="pt-BR" />
      <ReferenceArrayField source="stationIds" textAlign="center" reference="stations" >
        <Datagrid bulkActionButtons={false}>
          <TextField label="Postos" source="name" />
        </Datagrid>
      </ReferenceArrayField>
      <ReferenceArrayField source="fuelIds" textAlign="center" reference="fuels">
        <Datagrid bulkActionButtons={false}>
          <TextField label="Produtos" source="name" />
        </Datagrid>
      </ReferenceArrayField>
      <TextField label="Cliente" source="customerName" textAlign="center" />
    </Datagrid>
  );
};

const ListFillins = ({ ...props }) => {
  const translate = useTranslate();
  const fillinsRef = useRef();
  const record = useRecordContext();
  if (!record) { return null; }

  const listFilters = [
    <BetweenDatesInput alwaysOn />,
    <ReferenceArrayInput
      source="stationIds"
      reference="stations"
      perPage={null}
      sort={{ field: "name", order: "ASC" }}
      sx={listMarginSx}
      alwaysOn
    >
      <AutocompleteArrayInput
        filterToQuery={filterToQuery}
        label="Postos"
        sx={{ minWidth: 180, '& .MuiAutocomplete-inputRoot .MuiAutocomplete-input': { minWidth: 50 } }}
        optionText={"name"}
        noOptionsText={translate('ra.input.autocomplete.no_options_text')}
      />
    </ReferenceArrayInput>,
    <ReferenceArrayInput
      source="fuelIds"
      reference="fuels"
      sort={{ field: "name", order: "ASC" }}
      sx={listMarginSx}
      alwaysOn
    >
      <SelectArrayInput
        label="Produtos"
        style={{ minWidth: 180 }}
        optionText={"name"}
      />
    </ReferenceArrayInput>
  ];

  const stationsIndex = listFilters.findIndex(({ props }) => props.reference === 'stations');
  const placeId = getPlaceId();

  if (placeId && stationsIndex > -1) {
    listFilters.splice(stationsIndex, 1);
  }

  const ListFillinsActions = () => (
    <TopToolbar>
      <PrintButton
        printRef={fillinsRef}
      />
    </TopToolbar>
  );

  const filterValues: any = { skipJoins: true, customerId: record.id, showSummary: true };
  if (getEmployeeType() === EmployeeType.promoter) {
    filterValues.promoterId = getUserId();
  }

  return (
    <ListBase
      resource="fillins"
      sort={{ field: "createdAt", order: "DESC" }}
      filterDefaultValues={{
        from: moment().startOf("day").subtract(1, "month").toISOString(),
        to: moment().endOf("day").toISOString(),
        stationIds: placeId ? [placeId] : null
      }}
      perPage={25}
      filter={filterValues}
      disableSyncWithLocation
    >
      <ListToolbar
        filters={listFilters}
        actions={<ListFillinsActions />}
      />
      <ReportPrint ref={fillinsRef} title="Abastecimentos" landscape>
        <Box sx={filterTablePrintSx}>
          <Typography sx={titleCardSx}>Filtros</Typography>
          <FilterTablePrint customerName={record && record.name} />
        </Box>

        <FillinsDatagrids {...props} />
        <Pagination />
      </ReportPrint>
    </ListBase>
  );
};

const DrawingPanel = ({ customerId, ...props }) => {
  const record = useRecordContext();
  const filter = useMemo(() => ({ customerId, drawingId: record?.id }), [customerId, record?.id]);
  if (!record) { return null; }

  return (
    <List
      title=" "
      resource="drawing-lucky-numbers"
      sort={{ field: 'assignedAt', order: 'DESC' }}
      filter={filter}
      perPage={25}
      pagination={<Pagination />}
      actions={false}
      empty={
        <Box style={{ paddingTop: 30, paddingBottom: 30 }}>
          <Typography variant="subtitle1" gutterBottom align="center">Usuário não possui números</Typography>
        </Box>
      }
    >
      <Datagrid bulkActionButtons={false}>
        <TextField source="number" label="Número" />
        <DateField source="assignedAt" label="Ganhou em" showTime locales="pt-BR" emptyText="-" />
      </Datagrid>
    </List>
  );
}

const DrawingList = () => {
  const record = useRecordContext();
  if (!record) { return null; }

  return (
    <List
      title=" "
      resource={`chains/${record.chainId}/drawings`}
      filter={{ drawingCustomerId: record.id }}
      sort={{ field: 'createdAt', order: 'DESC' }}
      perPage={25}
      pagination={<Pagination />}
      actions={false}
      disableSyncWithLocation
    >
      <DrawingDatagrid bulkActionButtons={false} expand={<DrawingPanel customerId={record.id} />} disableEditButton disableDeleteButton />
    </List>
  );
};

const ReprocessPointsButton = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [loading, setLoading] = useState(false);
  const { permissions } = usePermissions();

  if (!record || !permissions?.includes(PERMISSIONS.ADMIN)) return null;

  const handleClick = async () => {
    if (!window.confirm('Tem certeza que deseja regerar o rastreamento de pontos deste cliente?')) return;
    setLoading(true);
    try {
      await dataProvider.create(`chains/chainId/customers/${record.id}/reprocess-points-tracking`, { data: {} });
      notify('Reprocessamento de pontos iniciado', { type: 'success' });
    } catch {
      notify('Erro ao iniciar reprocessamento', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button label="Regerar rastreamento de pontos" onClick={handleClick} disabled={loading} size="small" />
  );
};

const CouponsActions = () => (
  <TopToolbar>
    <ReprocessPointsButton />
  </TopToolbar>
);

const CouponsList = () => {
  const record = useRecordContext();
  if (!record) { return null; }

  return (
    <List
      title=" "
      resource="coupons"
      filter={{ customerId: record.id }}
      sort={{ field: 'createdAt', order: 'DESC' }}
      perPage={25}
      pagination={<Pagination />}
      actions={<CouponsActions />}
      disableSyncWithLocation
    >
      <Datagrid bulkActionButtons={false}>
        <FunctionField label="Produto" textAlign="center" render={record => isFreeCashbackCoupon(record) ? "Cashback" : record?.title || record?.product?.name || 'Não identificado'} />
        <TextField source="barcode" label="Cupom" textAlign="center" />
        <NumberField source="value" label="Pontos" textAlign="center" emptyText='-' />
        <NumberField source="partnerValue" label="Valor" textAlign="center" emptyText="R$ 0,00" options={{ style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 }} sortable={false} />
        <DateField source="redeemedAt" label="Data de troca" emptyText="-" textAlign="center" showTime locales="pt-BR" />
        <DateField source="createdAt" label="Data de geração" emptyText="-" textAlign="center" showTime locales="pt-BR" />
        <TextField source="place.name" label="Local de Retirada" sortable={false} />
        <BooleanField source="isGift" label="Brinde" />
        <RedeemCouponAdminButton />
        <RemoveCouponButton />
      </Datagrid>
    </List>
  );
};

const DevicesList = (props) => {

  const UpdateStateButton = () => {
    const refresh = useRefresh();
    const [update, { isLoading }] = useUpdate();
    const notify = useNotify();
    const record = useRecordContext();
    if (!record) { return null; }

    const handleClick = () => {
      update("device", {
        id: record.id,
        data: { state: record.state === DeviceStateType.blocked ? DeviceStateType.active : DeviceStateType.blocked },
        previousData: { ...record }
      }, {
        onSuccess: () => {
          notify(`Dispositivo ${record.state === DeviceStateType.blocked ? "ativo" : "bloqueado"} `, { type: "info" });
          refresh();
        },
        onError: () => {
          notify("Algo deu errado", { type: "warning" });
        }
      });
    }

    return (
      <Button
        label={record.state === DeviceStateType.blocked ? "Desbloquear" : "Bloquear"}
        onClick={handleClick}
        disabled={isLoading}
        variant="outlined"
      />
    );
  }
  return (
    <ReferenceManyField
      reference="chains/chainId/devices"
      addLabel={false}
      target="customerId"
      sort={{ field: 'createdAt', order: 'DESC' }}
      perPage={25}
      pagination={<Pagination />}
    >
      <Datagrid bulkActionButtons={false}>
        <TextField source="brand" label="Marca" />
        <TextField source="model" label="Modelo" />
        <SelectField
          source="state"
          label="Status"
          choices={DeviceStateType.DeviceStateTypeChoices}
        />
        <DateField source="createdAt" label="Data" showTime locales="pt-BR" />
        <DateField source="stateUpdatedAt" textAlign="center" label="Status atualizado em" emptyText='--' locales="pt-BR" />
        <ReferenceField emptyText="--" source="stateUpdatedBy" reference="employees" label="Atualizado por" link={false}>
          <TextField source="name" />
        </ReferenceField>
        <DeviceDisableButton />
        <UpdateStateButton />
      </Datagrid>
    </ReferenceManyField>
  );
}

const ReferenceCustomOneField: React.FC<any> = ({
  reference,
  source,
  target,
  children,
  label,
}) => {
  const [relatedRecord, setRelatedRecord] = useState(null);
  const record = useRecordContext();
  const filter = useMemo(() => ({ [target]: record?.[source] }), [target, record?.[source]]);
  const pagination = useMemo(() => ({ page: 1, perPage: 1 }), []);

  const { data, isLoading } = useGetList(reference, { filter, pagination });

  useEffect(() => {
    if (data && data.length > 0) {
      setRelatedRecord(data[0]);
    }
  }, [data]);

  if (!record?.[source]) { return <>Não informado</>; }

  if (isLoading || !relatedRecord) { return <></>; }

  return (
    <RecordContextProvider value={relatedRecord}>
      {children}
    </RecordContextProvider>
  );
}

const NotificationTokensList = (props) => {
  return (
    <ReferenceManyField
      reference="notification-tokens"
      target="customerId"
      sort={{ field: 'createdAt', order: 'DESC' }}
      perPage={25}
      pagination={<Pagination />}
    >
      <Datagrid bulkActionButtons={false}>
        <CopyToChipboardField
          Component={FunctionField}
          render={(record) => record && record.token ? `${record.token.slice(0, 8)} ...` : '--'}
          source="token"
          //@ts-ignore
          sortable={false}
        />
        <ReferenceCustomOneField
          reference="chains/chainId/devices"
          label="Dispositivo"
          target="uniqueId"
          source="deviceId"
          sortable={false}
        >
          <TextField source="deviceName" />
        </ReferenceCustomOneField>
        <ReferenceCustomOneField
          reference="chains/chainId/devices"
          label="Sistema"
          target="uniqueId"
          source="deviceId"
          sortable={false}
        >
          <TextField source="systemName" emptyText="--" />
        </ReferenceCustomOneField>
        <ReferenceCustomOneField
          reference="chains/chainId/devices"
          label="Versão"
          target="uniqueId"
          source="deviceId"
          sortable={false}
        >
          <TextField source="systemVersion" emptyText="--" />
        </ReferenceCustomOneField>
        <FunctionField
          render={(record) => record.valid ? 'Ativo' : 'Inativo'}
          label="Status"
          sortable={false}
        />
        <DateField source="createdAt" label="Data" showTime locales="pt-BR" />
      </Datagrid>
    </ReferenceManyField>
  );
}

const FraudLogList = (props) => {
  const record = useRecordContext();

  const PotencialFraudFilter = props => (
    <Filter {...props}>
      <SelectInput
        emptyText="Todos"
        source="isOpen"
        alwaysOn
        label='Situação'
        choices={[
          { id: true, name: 'Em aberto' },
          { id: false, name: 'Fechadas' }
        ]}
      />
    </Filter>
  );

  return (
    <List
      {...props}
      perPage={25}
      resource={`chains/${record.chainId}/potencial-frauds`}
      basePath={`chains/${record.chainId}/potencial-frauds`}
      pagination={<ListPagination />}
      exporter={false}
      title=" "
      filters={<PotencialFraudFilter />}
      filter={{ customerId: record.id }}
      filterDefaultValues={{ isOpen: true }}
      disableSyncWithLocation
    >
      <PotencialFraudDataGrid hideName />
    </List>
  );
}

const VehiclesList = () => {
  const record = useRecordContext();
  if (!record) { return null; }

  return (
    <List
      title=" "
      resource={`customers/${record.id}/vehicles`}
      sort={{ field: 'createdAt', order: 'DESC' }}
      perPage={25}
      pagination={<ListPagination />}
      actions={false}
      disableSyncWithLocation
    >
      <Datagrid bulkActionButtons={false}>
        <TextField source="plate" label="Placa" />
        <FunctionField
          label="Veículo"
          render={(record: any) => [record.brand, record.model, record.year].filter(Boolean).join(' ')}
        />
        <DateField source="createdAt" label="Cadastrado em" showTime locales="pt-BR" />
      </Datagrid>
    </List>
  );
};

const ReferedList = () => {
  const record = useRecordContext();
  if (!record) { return null; }

  return (
    <List
      title=" "
      resource="customers"
      filter={{ refereeCustomerId: record.id }}
      sort={{ field: "referedAt", order: "DESC" }}
      exporter={false}
      pagination={<ListPagination />}
      perPage={25}
      disableSyncWithLocation
    >
      <Datagrid bulkActionButtons={false}>
        <TextField source="name" label="Cliente Indicado" />
        <DateField source="referedAt" label="Data da Indicação" emptyText="-" textAlign="center" locales="pt-BR" />
        <ReferenceField reference="customer-first-fillin" source="id" label="Data do Abastecimento" sortable={false} emptyText="-" textAlign="center" >
          <DateField source="createdAt" locales="pt-BR" />
        </ReferenceField>
      </Datagrid>
    </List>
  )
}

const AdvertisementsLog = () => {
  const record = useRecordContext();
  if (!record) { return null; }
  const filterToQuery = searchText => ({ search: searchText });

  const listFilters = [
    <BetweenDatesInput alwaysOn />,
    <ReferenceArrayInput
      source="stationIds"
      reference="stations"
      perPage={25}
      sort={{ field: "name", order: "ASC" }}
      sx={listMarginSx}
      alwaysOn
    >
      <AutocompleteArrayInput label="Postos" optionText={"name"} filterToQuery={filterToQuery} style={{ minWidth: 220 }} />
    </ReferenceArrayInput>,
    <ReferenceArrayInput
      source="fuelIds"
      reference="fuels"
      sort={{ field: "name", order: "ASC" }}
      sx={listMarginSx}
      alwaysOn
    >
      <SelectArrayInput
        label="Combustíveis"
        style={{ minWidth: 180 }}
        optionText={"name"}
      />
    </ReferenceArrayInput>
  ];

  return (
    <List
      title=" "
      resource="advertisement-views"
      filter={{ customerId: record.id }}
      filters={listFilters}
      filterDefaultValues={{
        from: moment().startOf("day").subtract(1, "month").toISOString(),
        to: moment().endOf("day").toISOString(),
      }}
      actions={false}
      sort={{ field: "createdAt", order: "DESC" }}
      exporter={false}
      pagination={<ListPagination />}
      perPage={25}
      disableSyncWithLocation
    >
      <Datagrid bulkActionButtons={false}>
        <ReferenceField
          source="advertisementId"
          reference="advertisements"
          label="Anúncio"
          sortable={false}
          link={"show"}
          textAlign="center"
        >
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField
          source="stationId"
          reference="stations"
          label="Posto"
          sortable={false}
          link={false}
          textAlign="center"
        >
          <TextField source="name" />
        </ReferenceField>
        <ReferenceField
          source="fuelId"
          reference="fuels"
          label="Combustível"
          sortable={false}
          link={false}
          textAlign="center"
        >
          <TextField source="name" />
        </ReferenceField>
        <TextField source="systemName" label="Dispositivo" textAlign="center" />
        <DateField label="Data da visualização" locales="pt-BR" source="createdAt" showTime textAlign="center" />
      </Datagrid>
    </List>
  );
};

const CustomerNotes = () => {
  const record = useRecordContext();
  if (!record) { return null; }

  return (
    <ArrayField source="customerNotes" label="Anotações">
      <Datagrid bulkActionButtons={false}>
        <DateField source="createdAt" label="Data" textAlign="center" locales="pt-BR" />
        <ReferenceField textAlign="center" source="employeeId" sortable={false} reference="employees" label="Anotado por" link={false} >
          <TextField source="name" label="anotado por" />
        </ReferenceField>
        <TextField source="note" label="Anotação" sortable={false} />
      </Datagrid>
    </ArrayField>
  );
};

export default ({ ...props }) => {
  const employeeType = getEmployeeType();
  const { permissions } = usePermissions();
  const [displayCouponModal, setDisplayCouponModal] = useState(false);
  const [displayBalanceModal, setDisplayBalanceModal] = useState(false);
  const [displayNotificationModal, setDisplayNotificationModal] = useState(false);

  const ClientShowActions = (props) => (
    <TopToolbar>
      <Permission permission={PERMISSIONS.ADD_POINTS}>
        <Button
          variant="outlined"
          label="Adicionar Pontos"
          onClick={() => setDisplayBalanceModal(true)}
        />
      </Permission>
      <Permission permission={PERMISSIONS.CREATE_COUPON}>
        <Button
          variant="outlined"
          label="Adicionar Cupom"
          onClick={() => setDisplayCouponModal(true)}
        />
      </Permission>
      <Permission permission={PERMISSIONS.UNIFIED_SEND}>
        <Button
          sx={{ marginTop: 2 }}
          variant="outlined"
          label="Enviar notificação"
          onClick={() => setDisplayNotificationModal(true)}
        />
      </Permission>
      <Permission permission={PERMISSIONS.UPDATE_CUSTOMER}>
        <EditButton record={props.data} />
      </Permission>
    </TopToolbar>
  );

  const WrapperBlocked = () => {
    const record = useRecordContext();
    if (!record?.isBlocked) { return null; }
    return (
      <Labeled>
        <ReferenceField label="Bloqueado por" source="blockedByEmployeeId" reference="employees" emptyText="Não identificado" link={(record, reference) => {
          if (permissions && permissions.includes(PERMISSIONS.UPDATE_USER)) {
            return `/${reference}/${record.id}`
          }
          return '';
        }}>
          <FunctionField render={record => record ? `${record.name} ${record.id === getUserId() ? __t('(você)') : ''}` : ''} />
        </ReferenceField>
      </Labeled>
    )
  }

  const WrapperIgnoreLimits = () => {
    const record = useRecordContext();
    if (!record?.ignoreLimits) { return null; }
    return (
      <Box sx={{ display: 'flex', gap: 4 }}>
        <Labeled>
          <ReferenceField label="Liberado dos limites por" source="ignoredLimitsByEmployeeId" reference="employees" emptyText="Não identificado" link={(record, reference) => {
            if (permissions && permissions.includes(PERMISSIONS.UPDATE_USER)) {
              return `/${reference}/${record.id}`
            }
            return '';
          }}>
            <FunctionField render={record => record ? `${record.name} ${record.id === getUserId() ? __t('(você)') : ''}` : ''} />
          </ReferenceField>
        </Labeled>
        <Labeled>
          <DateField source="ignoredLimitsAt" label="Liberado dos limites em" showTime locales="pt-BR" emptyText="-" />
        </Labeled>
      </Box>
    )
  }

  return (
    <Show actions={<ClientShowActions />} title="Cliente" {...props} >
      {displayBalanceModal && <Permission permission={PERMISSIONS.ADD_POINTS}>
        <BalanceButton onClose={() => setDisplayBalanceModal(false)} />
      </Permission>
      }
      {displayCouponModal && <Permission permission={PERMISSIONS.CREATE_COUPON}>
        <AddCoupon onClose={() => setDisplayCouponModal(false)} />
      </Permission>
      }
      {displayNotificationModal && <Permission permission={PERMISSIONS.UNIFIED_SEND}>
        <CustomerUnifiedSendButton onClose={() => setDisplayNotificationModal(false)} />
      </Permission>}
      <TabbedShowLayout>
        <Tab label="Resumo" >
          <FunctionField
            render={record => record?.isBlocked && <Chip label="Bloqueado" color="error" />}
            addLabel={false}
          />
          <TextField source="id" label="ID" />
          <TextField source="name" label="Nome" />
          <PartiallyHiddenCpfField source="cpf" label="CPF" />
          <PhoneField source="cellphone" label="Telefone" />
          <EmailField source="email" />
          <ReferenceField label="Grupo" source="groupId" textAlign="center" emptyText="Sem grupo" reference="chains/chainId/customer-groups" link={false}>
            <TextField source="name" />
          </ReferenceField>
          {
            Permission({
              permission: PERMISSIONS.ADMIN,
              children: (
                <ReferenceField label="Rede" source="chainId" textAlign="center" reference="chains" link={false}>
                  <TextField source="name" />
                </ReferenceField>
              )
            })
          }
          <DateField source="createdAt" label="Criado em" textAlign="center" showTime locales="pt-BR" />
          {
            (employeeType === EmployeeType.admin || [EmployeeType.chain, EmployeeType.place].includes(employeeType)) &&
            <ReferenceField
              label="Pontos"
              source="id"
              reference="customer-balance"
              emptyText="0"
              link={false}
            >
              <FunctionField
                source="balance"
                render={record => formatPoints(+record?.balance)}
              />
            </ReferenceField>
          }
          <FunctionField label="Status" render={(record) => record.isBlocked ? 'Bloqueado' : 'Ativo'} />
          <WrapperBlocked />
          <WrapperIgnoreLimits />
        </Tab>

        {(permissions && permissions.includes(PERMISSIONS.CUSTOMER_STATEMENT)) &&
          <Tab label="Extrato" path="transactions" hidden>
            <ListStatements />
          </Tab>
        }
        {/* <Tab label="Extrato" path="statement">
        <ReferenceManyField
          reference="customers/customerId/transactions"
          target="customerId"
          addLabel={false}
          sort={{ field: "createdAt", order: "DESC" }}
          pagination={<Pagination />}
        >
          <Datagrid bulkActionButtons={false}>
            <NumberField source="value" label="Valor" />
            <FunctionField render={record => translateTransactionType(record.type)} label='Tipo' />
            <NumberField source="balance" label="Saldo Parcial" />
            <DateField source="createdAt" label="Data" showTime locales="pt-BR" />
          </Datagrid>
        </ReferenceManyField>
      </Tab> */}
        <Tab label="Abastecimentos" path="fillins">
          <ListFillins {...props} />
        </Tab>
        <Tab label="Veículos" path="vehicles">
          <VehiclesList />
        </Tab>
        <Tab label="Avaliações" path="ratings">
          <ReferenceManyField
            reference="ratings"
            addLabel={false}
            target="customerId"
            sort={{ field: 'createdAt', order: 'DESC' }}
            perPage={25}
            pagination={<ListPagination />}
          >
            <Datagrid {...props} bulkActionButtons={false}>
              <ReferenceField source="customerId" label="Usuário" reference="customers" link='show'>
                <TextField source="name" />
              </ReferenceField>
              <StarRatingField source="attendantRating" label="Avaliação do frentista" />
              <StarRatingField source="stationRating" label="Avaliação do Posto" />
              <TextField source="comment" label="Comentário" />
              <DateField source='createdAt' label='Data' showTime locales="pt-BR" />
              <ReferenceField label="Abastecimento" source="fillinId" reference="fillins" link='show' sortable={false}>
                <FunctionField render={() => 'Abastecimento'} />
              </ReferenceField>
            </Datagrid>
          </ReferenceManyField>
        </Tab>
        {
          (permissions && permissions.includes(PERMISSIONS.COUPONS_CODE)) &&
          <Tab label="Cupons" path="coupons" >
            <CouponsList />
          </Tab>
        }
        {
          (permissions && permissions.includes(PERMISSIONS.DRAWING)) &&
          <Tab label="Sorteios" path="drawings">
            <DrawingList />
          </Tab>
        }
        {
          (permissions && permissions.includes(PERMISSIONS.DEVICE)) &&
          <Tab label="Dispositivos" path="devices">
            <DevicesList />
          </Tab>
        }
        {
          (permissions && permissions.includes(PERMISSIONS.DEVICE)) &&
          <Tab label="Tokens" path="notification-tokens">
            <NotificationTokensList />
          </Tab>
        }
        {
          (permissions && permissions.includes(PERMISSIONS.POTENCIAL_FRAUD_LIST)) &&
          <Tab label="Segurança" path="frauds">
            <FraudLogList />
          </Tab>
        }
        <Tab path="refered" label="Indicações">
          <ReferedList />
        </Tab>
        <Tab path="achievements" label="Conquistas">
          <CustomerAchievementsList />
        </Tab>
        {
          (permissions && permissions.includes(PERMISSIONS.ADVERTISEMENT)) &&
          <CheckChainUsesModule module={ModuleKey.ADVERTISEMENT}>
            <Tab label="Anúncios Visualizados" path="advertisement-views">
              <AdvertisementsLog />
            </Tab>
          </CheckChainUsesModule>
        }
        <Tab label="Observações" path="notes">
          <CustomerNotes />
        </Tab>
        <Permission permission={PERMISSIONS.ADMIN}>
          <Tab label="Logs" path="logs">
            <ClientLogsTab />
          </Tab>
        </Permission>
      </TabbedShowLayout>
    </Show>
  );
}
